import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import Timeout from 'await-timeout';
import { COPY_STRINGS } from '../../../constants/translatedCopy';
import { getRandomArrayElement } from '../../../library';
import doNotUseAccessibleStaticElementInteractions from "A11y/doNotUseAccessibleStaticElementInteractions.js";
import QuizLayout from '../QuizLayout';
import QuizImageViewer from '../QuizImageViewer';
import { getUserData } from '../../../services/API';
import BookOverlay from '../../Global/BookOverlay/BookOverlay';
import { PhraseTriggers } from '../../../constants/constants';
import NonverbalReadTextButton from './NonverbalReadTextButton';
import { isSpanishStory } from '../../../services/locale';
import { assignmentTypes } from '../../../constants/constants';
import { watchShrinkToFit, applyShrinkToFit, MIN_QUESTION_FONT_SIZE_PX, QUESTION_TARGET_MAX_ROWS } from './quizTextScaling';

const QUESTION_SHRINK_LIMITS = {
  targetMaxRows: QUESTION_TARGET_MAX_ROWS,
  minFontSizePx: MIN_QUESTION_FONT_SIZE_PX,
};

function makeCancellable(promise) {
  let isCancelled = false;
  const wrappedPromise = new Promise((resolve, reject) => {
    promise
      .then((val) => (isCancelled ? reject({ isCancelled }) : resolve(val)))
      .catch((error) => (isCancelled ? reject({ isCancelled }) : reject(error)));
  });

  return {
    promise: wrappedPromise,
    cancel() {
      isCancelled = true;
    },
  };
}

const promptTimer = new Timeout();
const PROMPT_TIMEOUT = 10000;
const questionTimer = new Timeout();
const QUESTION_TIMEOUT = 10000;
const BOOK_OVERLAY_TIMEOUT = 240000; // 4 minutes

export default class MultipleChoice extends React.Component {
  isMounted = true;
  optionSelected = false;

  constructor(props){
    super(props);
    this.cancellablePromises = [];
    this.bookOverlayTimer = null;
    
    // Calculate extended timeouts if needed
    const extendedPromptTimeout = props.shouldExtendTimeout && props.timeoutExtensionMultiplier 
      ? Math.round(PROMPT_TIMEOUT * props.timeoutExtensionMultiplier)
      : PROMPT_TIMEOUT;
    const extendedQuestionTimeout = props.shouldExtendTimeout && props.timeoutExtensionMultiplier 
      ? Math.round(QUESTION_TIMEOUT * props.timeoutExtensionMultiplier)
      : QUESTION_TIMEOUT;
    
    // Determine if we should disable options during question/options speech
    // Only for Spanish stories + Spanish Pilot mode + Tutor mode
    const shouldDisableOptionsDuringReading = 
      props.story && 
      props.story.type === assignmentTypes.TUTOR &&
      props.isSpanishPilot &&
      isSpanishStory(props.story);
    
    this.state = {
      selected: [],
      tries: 0,
      selectionHistory: [],
      correct: false,
      highlighted: null,
      lastSelectedEncouragementSaid: null,
      storyOverlayOpen: this.props.nonverbalFlow,
      // TODO: These would be more correct as refs.
      optionsDisabled: shouldDisableOptionsDuringReading,
      speakingNonverbalInstructions: this.props.nonverbalFlow,
      speakingQuestion: false,
      speakingOptions: false,
      extendedPromptTimeout,
      extendedQuestionTimeout,
      showNonverbalBookOverlayContinueButton: this.props.nonverbalFlow,
      showOverlayPointer: false,
    };
    this.startPromptTimer = this.startPromptTimer.bind(this);
    this.startQuestionTimer = this.startQuestionTimer.bind(this);
    this.onDoneSpeakingQuestion = this.onDoneSpeakingQuestion.bind(this);
    this.onDoneSpeakingSelection = this.onDoneSpeakingSelection.bind(this);
    this.closeStoryOverlay = this.closeStoryOverlay.bind(this);
    this.openStoryOverlay = this.openStoryOverlay.bind(this);
    this.speakQuestion = this.speakQuestion.bind(this);
    this.onTitleClick = this.onTitleClick.bind(this);
    this.handleNonverbalBookOverlayButtonClick = this.handleNonverbalBookOverlayButtonClick.bind(this);
    this.startBookOverlayTimer = this.startBookOverlayTimer.bind(this);
    this.clearBookOverlayTimer = this.clearBookOverlayTimer.bind(this);

    this.enableOptionsTimeout = null;
    this.questionTitleEl = null;
    this.setQuestionTitleRef = (el) => {
      this.questionTitleEl = el;
    };
    this.stopWatchingQuestionShrink = null;
  }

  componentDidMount(){
    this.isMounted = true;
    promptTimer.clear();
    questionTimer.clear();
    this.stopWatchingQuestionShrink = watchShrinkToFit(() => this.questionTitleEl, QUESTION_SHRINK_LIMITS);

    if(this.props.nonverbalFlow){
      // Start the 4-minute timeout for nonverbal flow BookOverlay
      this.startBookOverlayTimer();
      return;
    }
    
    if(this.props.introText){
      this.props.speech.abortSpeech(() => {
        if (!this.isMounted) return;
        this.props.speech.speak({
          textToSpeak: this.props.introText,
          onEnd: () => {
            if (!this.isMounted) return;
            this.speakQuestion(this.onDoneSpeakingQuestion);
          },
        });
      });
    }else {
      //When no introduction, try checking if we're reading questions
      this.speakQuestion(this.onDoneSpeakingQuestion);
    }
    // if the options are disabled during reading, we need to start a timeout to enable them after 45 seconds as a backup option
    if (this.state.optionsDisabled) {
      this.enableOptionsTimeout = setTimeout(() => {
        this.setState({
          optionsDisabled: false,
        });
      }, 45000); // 45 seconds
    }
  }

  componentDidUpdate(prevProps, prevState){
    // Gate on the three inputs that actually change the rendered title: questionTitle,
    // state.selected (the filled-in blank for cloze questions), and props.image (which moves
    // the ref between the image/no-image layouts). applyShrinkToFit forces a synchronous
    // reflow, so re-running it on every update (this component has ~19 setState call sites,
    // several firing per-option during read-aloud) was a real perf cost, not just redundant work.
    if (
      prevProps.questionTitle !== this.props.questionTitle ||
      prevProps.image !== this.props.image ||
      prevState.selected !== this.state.selected
    ) {
      applyShrinkToFit(this.questionTitleEl, QUESTION_SHRINK_LIMITS);
    }
  }

  componentWillUnmount(){
    this.isMounted = false;
    this.cancelPromises();
    promptTimer.clear();
    questionTimer.clear();
    this.clearBookOverlayTimer();
    if (this.stopWatchingQuestionShrink) {
      this.stopWatchingQuestionShrink();
      this.stopWatchingQuestionShrink = null;
    }
    // clear timeout on unmount to prevent memory leaks
    if (this.enableOptionsTimeout) {
      clearTimeout(this.enableOptionsTimeout);
      this.enableOptionsTimeout = null;
    }
  }

  cancelPromises(){
    this.cancellablePromises.forEach(p => p.cancel());
    this.cancellablePromises = [];
  }

  cancellablePromise(p) {
    const cPromise = makeCancellable(p);
    this.cancellablePromises.push(cPromise);
    return cPromise.promise;
  }

  startBookOverlayTimer() {
    this.clearBookOverlayTimer();
    this.bookOverlayTimer = setTimeout(() => {
      if (this.isMounted && this.state.storyOverlayOpen) {
        this.handleNonverbalBookOverlayButtonClick();
      }
    }, BOOK_OVERLAY_TIMEOUT);
  }

  clearBookOverlayTimer() {
    if (this.bookOverlayTimer) {
      clearTimeout(this.bookOverlayTimer);
      this.bookOverlayTimer = null;
    }
  }

  closeStoryOverlay(){
    this.clearBookOverlayTimer();
    this.setState({
      storyOverlayOpen: false,
    }, () => {
      this.startPromptTimer();
    });
  }

  openStoryOverlay(){
    promptTimer.clear();
    questionTimer.clear();
    
    this.setState({
      storyOverlayOpen: true,
    });
  }

  startPromptTimer(){
    promptTimer.clear();
    questionTimer.clear();
    // Extend the prompt timer also, so that the overall assessment time is extended correctly
    promptTimer.set(this.state.extendedPromptTimeout).then(() => {
      if(this.state.selected.length == 0){
        if (!this.isMounted) return;
        this.props.speech.speak({
          textToSpeak: getRandomArrayElement(COPY_STRINGS.intervention_strings.comprehensionPrompt),
          onEnd: this.startQuestionTimer,
        });
      }
    });
  }

  startQuestionTimer(){
    questionTimer.clear();
    promptTimer.clear();
    questionTimer.set(this.state.extendedQuestionTimeout).then(() => {
      if (!this.isMounted) return;
      if(this.state.selected.length == 0){
        const response = " "; //make sure that if the student types nothing, the response is blank rather than null. TODO: find a less hacky way to do this
        this.props.updateResults(this.state.tries, this.state.selectionHistory, response, this.state.selected, this.state.correct);
      }
      this.props.questionTimeout();
    });
  }

  /**
   * Returns true when correctAnswer is an explicitly empty array,
   * meaning any answer should be accepted (survey-style question).
   */
  isAcceptAnyAnswer(){
    return Array.isArray(this.props.correctAnswer) && this.props.correctAnswer.length === 0;
  }

  isCorrect(selected){
    if(this.isAcceptAnyAnswer()){
      return selected.length > 0;
    }
    //If the arrays are not even the same length then it's not correct
    if(this.props.correctAnswer.length != selected.length){
      return false;
    }
    //If the arrays are the same length then compare every selected answer with the correctAnswer array
    for(let answer of selected){
      if(!this.props.correctAnswer.includes(answer))//If we have a wrong answer return false
        return false;
    }
    //If no wrong answers were found then return true
    return true;
  }

  async selectOption(optionSelected){
    this.setState({
      showOverlayPointer: false,
    });
    
    if (this.optionSelected) return;

    this.optionSelected = true;
    promptTimer.clear();
    questionTimer.clear();

    let getNewSelections = () => {
      if(this.isAcceptAnyAnswer()){
        return [optionSelected];
      }
      let newSelections = this.state.selected ? JSON.parse(JSON.stringify(this.state.selected)) : [];
      newSelections.push(optionSelected);
      if(newSelections.length > this.props.correctAnswer.length){
        newSelections.shift();
      }
      return newSelections;
    }

    let handleSelection = () => {
      if(!this.state.selected || !this.state.selected.includes(optionSelected) || this.state.lastSelectedEncouragementSaid !== optionSelected){
        let newSelections = getNewSelections();
        let correct = this.isCorrect(newSelections);
        this.setState({
          selected: newSelections,
          correct: correct,
          selectionHistory: this.state.selectionHistory.concat("selected " + optionSelected),
          speakingNonverbalInstructions: false,
          speakingQuestion: false,
        });
        return;
      }
    }

    let evaluateSelection = () => {
      const selected = this.state.selected || [];
      this.props.updateResults(this.state.tries, this.state.selectionHistory, optionSelected, selected, this.state.correct, () => {
        this.setState({
          lastSelectedEncouragementSaid: optionSelected,
          optionsDisabled:false, 
          speakingOptions: false, 
          speakingQuestion: false
        });
        if (!this.props.endOnCorrect || !this.state.correct) {
          this.optionSelected = false;
          this.startQuestionTimer();
        }
      })
    }

    if(this.isAcceptAnyAnswer()){
        this.cancelPromises();
        await new Promise(resolve => this.props.speech.abortSpeech(resolve));
        if (!this.isMounted) return;

        let newSelections = getNewSelections();
        let correct = this.isCorrect(newSelections);
        this.setState({
          selected: newSelections,
          correct: correct,
          selectionHistory: this.state.selectionHistory.concat('selected ' + optionSelected),
          speakingNonverbalInstructions: false,
          speakingQuestion: false,
        }, () => {
          const selected = this.state.selected || [];
          this.props.updateResults(this.state.tries, this.state.selectionHistory, optionSelected, selected, this.state.correct, () => {
            this.optionSelected = false;
          });
        });
    } else if(this.props.readQuestion){
        this.cancelPromises();
        await new Promise(resolve => this.props.speech.abortSpeech(resolve));

        if (!this.isMounted) return;

        this.setState({
          optionsDisabled: true,
          highlighted: null,
          speakingOptions: true,
          speakingQuestion: false,
        },handleSelection);

        await this.cancellablePromise(this.props.speech.speakWithPromise({
          textToSpeak: `"${optionSelected}".`,
          skipTranslation: getUserData().bilingualMode,
        }));

        if (!this.isMounted) return;
        evaluateSelection();
    } else {
      handleSelection();
    }
  }

  onDoneSpeakingSelection() {
    this.setState({ optionsDisabled: false, });
  }

  onTitleClick(){
    if (!this.state.speakingNonverbalInstructions && !this.props.isInstruction && !this.state.speakingQuestion && !this.state.speakingOptions) {
      this.speakQuestion(() => this.setState({ 
        speakingNonverbalInstructions: false,
        speakingQuestion: false, 
        speakingOptions: false, 
        optionsDisabled: false,
      }));
    }
  }

  speakQuestion(onEnd=null){
    if (!this.isMounted) return;
    if(!this.props.readQuestion){
      onEnd && onEnd();
      return;
    }

    this.props.speech.abortSpeech(() => {
      if(!this.state.speakingQuestion) {
        this.setState({
          speakingNonverbalInstructions: false,
          speakingQuestion: true,
        }, () => {
          let title = this.props.questionTitle;
          title = title.replace("____", "<BLANK/>"); //for backward compatibility
          let blank = COPY_STRINGS.blank;
          let speechProps = {
            textToSpeak: '<break time="0.5s"/>' + title.replace("<BLANK/>", "<break time=\"0.2s\" /> " + blank + " <break time=\"0.2s\" />"),
            passThroughSpeechTags: true,
            skipTranslation: getUserData().bilingualMode
          };
          if (onEnd) {
            speechProps.onEnd = onEnd;
          }
          if (!this.isMounted) return;
          this.props.speech.speak(speechProps);
        });
      }
    });
  }

  onDoneSpeakingQuestion(){
    if (!this.isMounted) return;
    this.setState({
      speakingQuestion: false,
      speakingOptions: false,
    }, async () => {
      console.log('speakingQuestion set to false');
      if(this.props.readQuestion){
        await this.cancellablePromise(this.speakAllOptions());
        if (!this.isMounted) return;
      }
      this.startPromptTimer();
    });
  }

  speakAllOptions(){
    if (!this.isMounted) return Promise.resolve();

    return new Promise((resolve) => {
      this.setState({
        speakingOptions: true, 
      }, async () => {
        for (const option of this.props.options) {
          this.setState({ highlighted: option });
          await this.cancellablePromise(
            this.props.speech.speakWithPromise({
              textToSpeak: `"${option}".`,
              skipTranslation: getUserData().bilingualMode,
            })
          )
          if (!this.isMounted) return;
        };

        this.setState({
          highlighted: null,
          speakingQuestion: false,
          speakingOptions: false,
          optionsDisabled: false,
        });
        // If the options were disabled during reading, we need to clear the timeout
        if (this.enableOptionsTimeout) {
          clearTimeout(this.enableOptionsTimeout);
          this.enableOptionsTimeout = null;
        }

        resolve();
      });
    });
  }

  handleNonverbalBookOverlayButtonClick() {
    this.clearBookOverlayTimer();
    this.setState({
      storyOverlayOpen: false,
      showNonverbalBookOverlayContinueButton: false,
    });
    
    // Play the nonverbal audio
    this.props.speech.speak({
      textToSpeak: COPY_STRINGS.reading_comprehension_intro_NON_VERBAL_THREE,
      onEnd: () => {
        this.setState({ showOverlayPointer: true });
        this.props.speech.speak({
          textToSpeak: COPY_STRINGS.reading_comprehension_intro_NON_VERBAL_FOUR,
          onEnd: () => {
            this.setState({ showOverlayPointer: false });
            this.props.speech.speak({
              textToSpeak: COPY_STRINGS.reading_comprehension_intro_NON_VERBAL_FIVE,
              onEnd: () => {
                this.setState({ speakingNonverbalInstructions: false });
                this.speakQuestion(this.onDoneSpeakingQuestion);
              }
            });
          }
        });
      }
    });
  }

  getOptions(){
    const optionsContainerClasses = cx({
      "multiple-options": true,
      "flat": this.props.flat,
      "keepTall": this.props.keepTall,
    });
    let optionClasses = {
      "labelFontOverride": true,
      "option": true,
    };

    const accessibleOptionGroupAttributes = {
      role: "radiogroup",
      "aria-required": true,
      "aria-label": "options",
    }
    const getAccessibleOptionAttributes = option => {
      return {
        ...doNotUseAccessibleStaticElementInteractions({onClick: onOptionClick.bind(this, option)}),
        role: "radio",
        "aria-label": option,
        "aria-checked": this.state.selected.includes(option),
      }
    }

    const onOptionClick = this.state.optionsDisabled ? () => {} : this.selectOption;

    if(this.props.flat){
      return(
        <div className={optionsContainerClasses} {...accessibleOptionGroupAttributes}>
          {this.props.options.map((option, index) => {
            optionClasses.selected = this.state.selected.includes(option);
            optionClasses.highlightedOption = optionClasses.selected || this.state.highlighted == option;
            optionClasses.inactive = !optionClasses.selected && this.state.optionsDisabled;
            return (
              <span className={cx(optionClasses)} key={option} {...getAccessibleOptionAttributes(option)}>
                {option}
              </span>
            );
          })}
        </div>
      );
    } else {
      return(
        <div className={optionsContainerClasses}>
          <ol className="list" {...accessibleOptionGroupAttributes}>
          {this.props.options.map((option, index) => {
            optionClasses.selected = this.state.selected.includes(option);
            optionClasses.highlightedOption = optionClasses.selected;
            optionClasses.inactive = !optionClasses.selected && this.state.optionsDisabled;
            return (
              <li key={option}>
                <span className={cx(optionClasses)} {...getAccessibleOptionAttributes(option)}>
                  {option}
                </span>
              </li>
            );
          })}
          </ol>
        </div>
      );
    }
  }

  render(){
    const { questionTitle } = this.props;
    let title = questionTitle;
    title = title.replace("____", "<BLANK/>"); //for backward compatibility
    let splitTitle = title.split("<BLANK/>");
    if(splitTitle.length == 2){
      title = (
        <span>
          {splitTitle[0]}
          <span className={`underline ${false /*this.state.correct*/ ? "correct" : ""}`}>
            {this.state.selected && this.state.selected.length == 1 && this.state.selected[0]}
          </span>
           {splitTitle[1]}
        </span>);
    } else {
      title = title.replace("<BLANK/>", "_______");
    }

    let titleClasses = cx({
      "question-title": true,
      "labelFontOverride": true,
      "clickable": this.props.readQuestion,
    });

    const storyOverlayButton = this.props.canViewReferenceStory && this.props.referenceStoryPhrases && this.props.referenceStoryPhrases.length ? (
      <div style={{marginRight: "25px"}}>
        <NonverbalReadTextButton 
          onClick={this.openStoryOverlay}
          disabled={this.props.isInstruction || this.state.speakingNonverbalInstructions || this.state.speakingQuestion || this.state.speakingOptions} 
          small
          label={null}
          showPointer={this.state.showOverlayPointer || this.props.currentIntroductionTrigger === PhraseTriggers.HighlightReferenceStoryBtn}
          pointerPosition="left"
        />
      </div>
    ) : null;

    return (
      <div className="multiple-wrapper">
        {this.state.storyOverlayOpen && (
          <BookOverlay phrases={this.props.referenceStoryPhrases} onClose={this.closeStoryOverlay} showNonverbalBookOverlayContinueButton={this.state.showNonverbalBookOverlayContinueButton} onClickNonverbalBookOverlayButton={this.handleNonverbalBookOverlayButtonClick} closeOnEscapeOrOutsideClick />
        )}

        <QuizLayout
          left={this.props.image && (
            <div>
              <QuizImageViewer image={this.props.image} />
              <div className="question-title-wrapper">
                {storyOverlayButton}
                <div
                  className={titleClasses}
                  ref={this.setQuestionTitleRef}
                  {...doNotUseAccessibleStaticElementInteractions({
                    onClick: this.onTitleClick
                  })}
                >
                {title}
                </div>
              </div>
            </div>
          )}
          right={this.props.image && this.getOptions()}
          top={!this.props.image && (
            <div className="question-title-wrapper">
              {storyOverlayButton}
              <div className={titleClasses} ref={this.setQuestionTitleRef} {...doNotUseAccessibleStaticElementInteractions({ onClick: this.onTitleClick })}>
                {title}
              </div>
            </div>
          )}
          bottom={!this.props.image && this.getOptions()}
        />
      </div>
    );
  }
}

MultipleChoice.propTypes = {
  options: PropTypes.array.isRequired,
  correctAnswer: PropTypes.array.isRequired,
  questionTitle: PropTypes.string,
  updateResults: PropTypes.func.isRequired,
  allowRetry: PropTypes.bool,
  speech: PropTypes.object.isRequired,
  introText: PropTypes.string,
  readQuestion: PropTypes.bool,
  flat: PropTypes.bool,
  keepTall: PropTypes.bool,
  questionTimeout: PropTypes.func,
  endOnCorrect: PropTypes.bool,
  isInstruction: PropTypes.bool,
  canViewReferenceStory: PropTypes.bool,
  referenceStoryPhrases: PropTypes.array,
  currentIntroductionTrigger: PropTypes.string,
  shouldExtendTimeout: PropTypes.bool,
  timeoutExtensionMultiplier: PropTypes.number,
  image: PropTypes.string,
  nonverbalFlow: PropTypes.bool,
  story: PropTypes.object,
  isSpanishPilot: PropTypes.bool,
};